import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck, Loader2, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { adminLatestSecurityScan, adminRunSecurityScan } from '../../api';

const SEV = { CRITICAL: 'bg-red-500/15 text-red-300', HIGH: 'bg-orange-500/15 text-orange-300', MODERATE: 'bg-amber-500/15 text-amber-300', MEDIUM: 'bg-amber-500/15 text-amber-300', LOW: 'bg-slate-500/15 text-slate-300' };
const fmt = (d) => d ? new Date(d.endsWith('Z') || d.includes('+') ? d : d + 'Z').toLocaleString() : '';

export const SecurityScanCard = () => {
  const { toast } = useToast();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => adminLatestSecurityScan().then(setScan).catch(() => setScan(null)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (scan?.status !== 'running') return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [scan?.status, load]);

  const run = async () => {
    try { await adminRunSecurityScan(); toast({ title: 'Scan started', description: 'Results appear here in about a minute.' }); load(); }
    catch (err) { toast({ title: 'Could not start scan', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  const running = scan?.status === 'running';
  const findings = scan?.findings || [];

  return (
    <div data-testid="security-scan-card" className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-7">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          {findings.length ? <ShieldAlert className="w-5 h-5 text-amber-400" /> : <ShieldCheck className="w-5 h-5 text-cyan-400" />}
          <h3 className="text-xl font-semibold text-white">Security Scan</h3>
        </div>
        <Button data-testid="security-scan-run-btn" onClick={run} disabled={running} className="h-8 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold">
          {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}{running ? 'Scanning...' : 'Run scan now'}
        </Button>
      </div>
      <p className="text-sm text-slate-400 mb-5">Runs automatically at 2:00 AM Eastern on the 1st of each month. It checks every server and website package against public lists of known security flaws. Fixes go live when the site is redeployed.</p>
      {loading ? <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading...</div>
        : !scan ? <p data-testid="security-scan-empty" className="text-sm text-slate-500">No scan has run yet.</p>
        : (
          <>
            <p data-testid="security-scan-status" className="text-sm text-slate-300 mb-4">
              Last scan: {fmt(scan.created_at)} ({scan.trigger}) &middot; {scan.status === 'failed' ? <span className="text-red-400">failed: {scan.error}</span> : running ? 'in progress' : <>{scan.packages_scanned} packages checked &middot; <span className={findings.length ? 'text-amber-300' : 'text-emerald-400'}>{findings.length} issue{findings.length === 1 ? '' : 's'} found</span></>}
            </p>
            {findings.length > 0 && (
              <div className="space-y-2 max-h-[480px] overflow-y-auto">
                {findings.map(f => (
                  <div key={`${f.package}-${f.vuln_id}`} data-testid={`security-finding-${f.vuln_id}`} className="rounded-md border border-cyan-400/10 bg-[#080d18] px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${SEV[f.severity?.toUpperCase()] || SEV.LOW}`}>{f.severity}</span>
                      <span className="text-sm text-white font-mono">{f.package} {f.version}</span>
                      <span className="text-xs text-slate-500">{f.ecosystem === 'PyPI' ? 'server' : 'website'}</span>
                      <a href={`https://osv.dev/vulnerability/${f.vuln_id}`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline ml-auto">{f.vuln_id}</a>
                    </div>
                    <p className="text-xs text-slate-400">{f.summary || 'No description provided.'}</p>
                    <p className="text-xs text-slate-300 mt-1">{f.fixed_in ? <>Fix: update to <span className="font-mono text-cyan-300">{f.fixed_in}</span> or later</> : 'No fixed version published yet'}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
    </div>
  );
};

export default SecurityScanCard;
